import { defineComponent, FieldViewModel, getDistinctValues, getFieldCroppingArea, handleApiError, IField, IOmniaShell, isNumber, ISoilType, isRegionInNvz, ISubsoilType, LocaleString, omniaResolve, param, utils } from "../omnia"
import { IFieldEditModel, IFieldsEditService, IFieldsService } from "../types"
import { getFieldActivity } from "./activity"
import { EditCtrl } from "./edit"
import { FieldDrawingContext } from "./field/drawingContext"
import { getFieldErrorMessages } from "./field/errors"
import { createSoilEditController } from "./field/soil"
import { ChangeConfirmation, confirmChanges } from "./field/validate"

/// <reference path="edit.ts" />
/// <reference path="field/validate.ts" />

import m = omnia.models;

interface IAreaType {
    value: m.FieldAreaType,
    text: string
}



export function createFieldEditor(): ng.IDirective {

    return defineComponent(EditFieldCtrl, "app/fields/edit/field.html", {
        fieldsMap: "="
    });

};

export class EditFieldCtrl extends EditCtrl<IField> {

    readonly errorMessages = getFieldErrorMessages(this)

    soilTypes: ISoilType[]
    subsoilTypes: ISubsoilType[]

    fieldGroups: string[]

    areaTypes: IAreaType[]
    targetAreaType: IAreaType

    soilErosionRisks = param.SoilErosionRisk()
    windErosionRisks = param.WindErosionRisk()
    fieldDrainage = param.FieldDrainage()

    /** Defines which inputs are locked/unlocked - only used in "multipleTargets" mode */
    readonly locked = {
        fieldGroup: true,
        soil: true,
        subsoil: true,
        soilErosion: true,
        windErosion: true,
        fieldDrainage: true,
        waterCourse: true,
        sensitiveHabitat: true,
        ancientMonument: true,
        psc: true
    }

    public readonly soil = createSoilEditController(this)

    public readonly enablePsc = this.svc.holding.Region === m.RegionId.Scotland

    private _initialArea: number
    private _initialField: IField

    public allFields: FieldViewModel[]
    public filteredFields: FieldViewModel[]

    public fieldsFilter: string

    public readonly canChangeNvz = !isRegionInNvz(this.svc.holding.Region)


    static $inject = ["$scope", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapTopologyService", "OmniaShell"];

    constructor($scope: ng.IScope, svc: IFieldsService, editSvc: IFieldsEditService, protected topo: omnia.map.IMapTopologyService, private shell: IOmniaShell) {

        super($scope, svc, editSvc, topo, shell.isReadOnly(p => p.fields))

    }

    protected setupTarget() {

        this.setActivity(getFieldActivity(this._create, this.target))

        this._initialField = { ...this.target }

        this.fieldGroups = getDistinctValues(this.svc.getFields(true).filter(f => f.fieldGroup), f => f.fieldGroup).sort((a, b) => a.localeCompare(b))

        this.allFields = this.filteredFields = this.svc.fields

        this.setupSoilTypes()

        this.setupAreas()

        this.setupNVZ()

    }

    private setupSoilTypes() {

        this.soilTypes = this.svc.soilTypes;
        this.subsoilTypes = this.svc.subsoilTypes;

        if (this.target.SoilType) {
            this.target.SoilType = <any>this.soilTypes.find(c => c.SoilTypeId == this.target.SoilType.SoilTypeId);
        }

        if (this.target.SubsoilType) {
            this.target.SubsoilType = <any>this.subsoilTypes.find(c => c.SubsoilTypeId == this.target.SubsoilType.SubsoilTypeId);
        }

    }

    private setupAreas() {

        this.areaTypes = [

            { value: m.FieldAreaType.WholeArea, text: "Whole area" as LocaleString },
            { value: m.FieldAreaType.WorkingArea, text: "Working area" as LocaleString },
            { value: m.FieldAreaType.BoundaryArea, text: "Boundary area" as LocaleString },

        ];


        this.targetAreaType = this.availableAreaTypes.find(at => at.value == this.target.AreaType);

        this._initialArea = this.newField ? null : getFieldCroppingArea(this.target);

    }

    private setupNVZ() {

        if (this.newField) {

            var holding = this.svc.holding;

            if (holding) {
                this.target.NVZ = holding.NVZ;
            }

        }

    }

    get newField(): boolean {
        return this._create;
    }

    get hasBoundary(): boolean {

        if (this._editBoundaryVm) {
            if (this._editBoundaryVm.deleted) return false
            if (this._editBoundaryVm.hasChanged) return true
        }

        return (this.target && !!this.target.Boundary)

    }

    get boundaryArea(): number {

        var area: number;

        if (this._editBoundaryVm) {
            area = this._editBoundaryVm.getBoundaryArea();
            if (area != null) area = Math.round(area / 100) / 100;
        }

        if (area == null) area = this.target.BoundaryArea;

        return area;

    }

    get availableAreaTypes(): IAreaType[] {
        if (this.areaTypes) {
            return this.hasBoundary ? this.areaTypes : this.areaTypes.filter(t => t.value != m.FieldAreaType.BoundaryArea);
        }
    }

    get useWholeArea(): boolean {
        return this.targetAreaType && (this.targetAreaType.value == m.FieldAreaType.WholeArea);
    }

    get useWorkingArea(): boolean {
        return this.targetAreaType && (this.targetAreaType.value == m.FieldAreaType.WorkingArea);
    }

    get useBoundaryArea(): boolean {
        return this.targetAreaType && (this.targetAreaType.value == m.FieldAreaType.BoundaryArea);
    }

    get effectiveArea(): number {

        var t = this.targetAreaType;

        if (t) {

            switch (t.value) {

                case m.FieldAreaType.WholeArea: return this.target.WholeArea;
                case m.FieldAreaType.WorkingArea: return this.target.WorkingArea;
                case m.FieldAreaType.BoundaryArea: return this.boundaryArea;

            }
        }
    }

    getFieldArea(field: IField) {
        return getFieldCroppingArea(field)
    }

    validateWorkingArea() {

        const { target: { WholeArea: whole, WorkingArea: working } } = this

        const ok = !(isNumber(working) && isNumber(whole) && (working > whole))

        const target: ng.INgModelController = this.form["WorkingArea"]
        target?.$setValidity("area", ok)


    }

    validateAreas() {
        const { target } = this
        this.validateArea(target.NitrogenSpreadArea, "NitrogenSpreadArea")
        this.validateArea(target.ManureSpreadArea, "ManureSpreadArea", this.hasCalculatedManureArea())
    }

    validateArea(area: number, attr: string, isCalculated = false): boolean {

        const { effectiveArea } = this

        const ok = isCalculated || !(isNumber(effectiveArea) && isNumber(area) && (area > effectiveArea))

        const target: ng.INgModelController = this.form[attr]
        target?.$setValidity("area", ok)

        return ok

    }

    get canEnableMultipleTargets() {

        const { newField, _initialField: initField, _editBoundaryVm: boundary, _multipleTargets: multiple, readOnly } = this

        if (readOnly) return false
        if (multiple) return true
        if (newField) return false
        if (!initField) return false
        if (boundary?.isActive || boundary?.hasChanged) return false

        const keys = [
            "Name",
            "FieldNumber",
            "AreaType",
            "WholeArea",
            "WorkingArea",
            "BoundaryArea",
            "NitrogenSpreadArea",
            "ManureSpreadArea",
            "NVZ"
        ]

        const init = {
            ...this._initialField,
            NVZ: !!this._initialField.NVZ
        }

        const current = {
            ...this.target,
            NVZ: !!this.target.NVZ,
            AreaType: this.targetAreaType ? this.targetAreaType.value : undefined
        }

        return keys.every(k => init[k] == current[k])

    }

    setMultipleTargets(value: boolean) {

        this.editSvc.multiple = value

        if (value != this._multipleTargets) {

            this._multipleTargets = value

            if (!value) {
                this.svc.selectSingleField(this.target.FieldId)
            }

            this._editBoundaryVm.enableMultiSelection(value)

        }

    }

    getTargets() {
        return this.multipleTargets
            ? this.editSvc.fields
            : [this.target]
    }

    filterFields(filter: string) {

        const re = filter
            ? new RegExp(utils.regexp.escape(filter), "i")
            : undefined


        this.filteredFields = this.allFields.filter((f: FieldViewModel) => {
            return f.selected || (!re) || (re.test(f.fieldName))
        })

    }

    canSelectAll() {
        return this.filteredFields.some(f => !f.selected)
    }

    canUnselectAll() {
        return this.filteredFields.every(f => f.selected)
    }

    selectAll(select: boolean) {
        for (const f of this.filteredFields) {
            f.selected = select
        }
        this.syncSelectedTargetsToMap()
    }

    syncSelectedTargetsToMap() {
        this.svc.selectItems(this.allFields.filter(f => f.selected), true)
    }

    private confirmChanges(target: IField): ng.IPromise<ChangeConfirmation> {

        if (this.newField) {
            return omniaResolve({})
        }

        if (this._multipleTargets) {
            return omniaResolve({})
        }
        const drawingContext = (this.editBoundaryViewModel as FieldDrawingContext)

        const hasNewUncroppedAreas = drawingContext.hasNewUncroppedAreas()

        return confirmChanges({
            shell: this.shell,
            svc: this.svc,
            target,
            fieldId: this.target.FieldId,
            initialArea: this._initialArea,
            hasNewUncroppedAreas
        })

    }

    revalidate() {
        this.validateWorkingArea()
        this.validateAreas()
    }

    commitEdit() {

        this._showValidationErrors = true

        if (!this.canCommit()) {
            return
        }

        if (!this._editBoundaryVm.validate()) {
            return
        }

        this._showValidationErrors = false

        const model = this.getUpdateModel()
        const multiple = this._multipleTargets

        return this.confirmChanges(model).then(conf => {

            conf = conf || {}

            if (!conf.cancel) {

                const options = model.EditOptions

                if (!multiple) {
                    options.UpdateCropArea = conf.changeCroppingArea
                    options.CreateUncroppedAreas = conf.createUncroppedAreas
                    options.UpdateCroppingBoundaries = !conf.createUncroppedAreas
                }

                let result: ng.IPromise<any>;

                if (this.newField) {
                    result = this.svc.createField(model)
                }
                else {
                    const fieldIds = this.getTargets().map(f => f.FieldId)
                    result = this.svc.updateFields(fieldIds, model)
                }

                result
                    .then(r => this.cancelEdit())
                    .catch(r => handleApiError(this.shell, r, { resourceType: "field" }))

            }

        })

    }

    private getUpdateModel(): IFieldEditModel {

        const { target: t, locked, multipleTargets } = this

        const shouldSet = (key: keyof (typeof locked)) => {
            return multipleTargets ? !locked[key] : true;
        }

        const boundary = this._editBoundaryVm && this._editBoundaryVm.getBoundaryData()

        const data: IFieldEditModel = {

            EditOptions: {
                UpdateCropArea: false,
                CreateUncroppedAreas: false,
                UpdateCroppingBoundaries: true,
                DeleteBoundary: boundary && boundary.DeleteBoundary
            },

            FieldId: null,

            Name: t.Name,
            FieldNumber: t.FieldNumber,
            FieldGroup: shouldSet("fieldGroup") ? (t.FieldGroup ?? "") : null,

            HoldingId: t.HoldingId,
            FirstYearId: t.FirstYearId,

            AreaType: (this.targetAreaType ? this.targetAreaType.value : t.AreaType),

            WholeArea: t.WholeArea,
            WorkingArea: t.WorkingArea,
            BoundaryArea: t.BoundaryArea,

            NitrogenSpreadArea: t.NitrogenSpreadArea,
            ManureSpreadArea: t.ManureSpreadArea,

            NVZ: t.NVZ,

            SoilType: null,
            SoilTypeId: shouldSet("soil") ? (t.SoilType ? t.SoilType.SoilTypeId : null) : null,

            SubsoilType: null,
            SubsoilTypeId: shouldSet("subsoil") ? (t.SubsoilType ? t.SubsoilType.SubsoilTypeId : null) : null,

            SoilErosionRisk: shouldSet("soilErosion") ? t.SoilErosionRisk : -1,
            WindErosionRisk: shouldSet("windErosion") ? t.WindErosionRisk : -1,
            FieldDrainage: shouldSet("fieldDrainage") ? t.FieldDrainage : -1,
            WaterCourse: shouldSet("waterCourse") ? (t.WaterCourse ?? false) : null,
            SensitiveHabitat: shouldSet("sensitiveHabitat") ? (t.SensitiveHabitat ?? false) : null,
            AncientMonument: shouldSet("ancientMonument") ? (t.AncientMonument ?? false) : null,

            PSorpCapacity: shouldSet("psc") ? t.PSorpCapacity : null,

            Cropping: null,
            Boundary: null,

        };

        if (boundary && (!boundary.DeleteBoundary)) {
            data.Boundary = <any>boundary.Boundary
            data.BoundaryArea = boundary.BoundaryArea
        }

        return data;

    }

    createBoundaryEditContext() {

        return new FieldDrawingContext(
            this.target,
            id => this.svc.canDeleteField(id),
            () => this.targetAreaType?.value,
            this.fieldsMap,
            this.topo,
            this.shell,
            () => this.svc.setView(true),
            this._drawingSettings
        )

    }

}
